import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import {
  addToItems,
  changeQty,
  db,
  findByBarcode,
  quickOrderForTable,
  saveItems,
  servicePercentText,
  topProductsToday,
  type OrderItem,
  type PayMethod,
  type Product,
} from '../lib/db'
import { bare } from '../lib/menuImages'
import { useBarcodeScanner } from '../lib/scanner'
import { money, qtyToText, QTY_UNIT } from '../lib/money'
import { useApp } from '../lib/store'
import { DiscountSheet } from '../components/DiscountSheet'
import { NoteSheet } from '../components/NoteSheet'
import { WeightSheet } from '../components/WeightSheet'
import { Thumb } from '../components/Thumb'
import { PageHead } from '../components/PageHead'

/**
 * Bán nhanh tại quầy, bộ Modern.
 *
 * Hai khoang: lưới món bên trái, giỏ hàng bên phải. Bán tại quầy là gõ liên tục — khách
 * đứng đợi ngay trước mặt — nên món và giỏ phải cùng nằm trên một màn hình, không có
 * bước chuyển màn hình nào ở giữa.
 *
 * Đơn của màn hình này gắn vào ô "Mang về" như bộ Classic vẫn làm, chứ không đẻ ra một
 * khái niệm đơn mới: đơn mang về hiện luôn trên sơ đồ bàn, và mọi thứ đã dựng quanh đơn
 * hàng — đồng bộ, hoá đơn, ca làm — chạy tiếp không phải sửa gì.
 */
/** Ba hàng bốn cột. Quầy có chỗ rộng hơn màn gọi món nên bày được nhiều hơn một hàng. */
const PAGE_SIZE = 12

export function QuickSellScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)

  const [orderId, setOrderId] = useState<string | null>(null)
  const [catId, setCatId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [method, setMethod] = useState<PayMethod>('Cash')
  const [weighing, setWeighing] = useState<Product | null>(null)
  const [discounting, setDiscounting] = useState(false)
  const [noting, setNoting] = useState<OrderItem | null>(null)
  const [pageAt, setPageAt] = useState(1)
  const [searching, setSearching] = useState(false)

  const categories = useLiveQuery(() => db.categories.orderBy('sortOrder').toArray(), [], [])
  const products = useLiveQuery(() => db.products.orderBy('sortOrder').toArray(), [], [])
  const order = useLiveQuery(() => (orderId ? db.orders.get(orderId) : undefined), [orderId])
  const top = useLiveQuery(() => topProductsToday(3), [], [])

  // Mỗi lượt vào Bán nhanh là một lượt khách, nên là một đơn riêng — trừ khi lần trước
  // thoát ra mà chưa gọi món gì, thì dùng lại cái vỏ rỗng đó.
  // Đơn vẫn treo ở ô Mang về như cũ, nên nó hiện luôn trong hàng đợi của màn hình Mang về.
  useEffect(() => {
    let alive = true
    void (async () => {
      const takeaway =
        (await db.diningTables.filter((t) => t.isTakeaway).first()) ??
        (await db.diningTables.toCollection().first())
      if (!takeaway) {
        say('Quán chưa có bàn nào — vào Sơ đồ bàn thêm một ô Mang về')
        return
      }
      const o = await quickOrderForTable(takeaway)
      if (alive) setOrderId(o.id)
    })()
    return () => {
      alive = false
    }
  }, [say])

  const hotNames = useMemo(() => new Set(top.map((t) => t.name)), [top])

  const listed = useMemo(() => {
    // So sau khi bỏ dấu: đứng bán ở quầy thì không ai gõ đủ dấu.
    const q = bare(query)
    return products.filter((p) => {
      if (q) return bare(p.name).includes(q) || (p.barcode ?? '').includes(q)
      return catId === null || p.categoryId === catId
    })
  }, [products, catId, query])

  // Đổi nhóm hoặc gõ chữ tìm là về trang đầu — đứng ở trang 3 của nhóm cũ rồi nhảy sang
  // nhóm chỉ có một trang thì màn hình trống trơn.
  useEffect(() => {
    setPageAt(1)
  }, [catId, query])

  /*
   * Phân trang thay cho cuộn.
   *
   * Bán tại quầy là gõ liên tục với khách đứng ngay trước mặt. Cuộn thì lần nào cũng phải
   * tìm lại chỗ; số trang thì đứng yên, nhớ một buổi là bấm không cần nhìn. Hiện hết số,
   * không rút gọn bằng dấu ba chấm — rút gọn thì nút số 5 đổi chỗ theo trang đang đứng.
   */
  const pageCount = Math.max(1, Math.ceil(listed.length / PAGE_SIZE))
  const page = Math.min(pageAt, pageCount)
  const shown = listed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const items = order?.items ?? []

  /*
   * Số dòng và số phần là hai con số khác nhau.
   *
   * Khách mua bốn ly cùng một món thì bốn ly ấy nằm gọn trên một dòng. Người pha cần con
   * số phần, không phải số dòng — thiếu nó là đưa ra ba ly rồi khách quay lại hỏi.
   * Chỉ hiện thêm khi hai số khác nhau: "4 món · 4 phần" là hai lần cùng một thông tin.
   */
  const pieces = items.reduce((sum, i) => sum + i.qtyMilli, 0)
  const countText =
    pieces > items.length * QTY_UNIT
      ? `${items.length} món · ${qtyToText(pieces)} phần`
      : `${items.length} món`

  /** Riêng một biến thể đã gọi bao nhiêu — con số ở góc thẻ là tổng cả món. */
  const qtyOfOption = (productId: string, option: string) =>
    items
      .filter((i) => i.productId === productId && i.optionLabel === option)
      .reduce((s, i) => s + i.qtyMilli, 0)

  // Dòng trong giỏ chỉ giữ tên và giá lúc bán, không giữ mã ảnh — tra ngược ra sản phẩm.
  const imageOf = (productId: string | null) =>
    productId ? (products.find((x) => x.id === productId)?.imageKey ?? null) : null

  async function add(p: Product, option: string | null) {
    if (!order) return
    if (p.soldByWeight) {
      setWeighing(p)
      return
    }
    await saveItems(order.id, addToItems(items, p, option))
  }

  // Máy scan quét mã là thêm thẳng vào giỏ. Tắt khi đang mở bảng cân hoặc bảng giảm giá —
  // lúc đó người dùng đang gõ số, không phải đang quét hàng.
  useBarcodeScanner({
    enabled: !weighing && !discounting && !noting,
    onScan: async (code) => {
      const found = await findByBarcode(code)
      if (!found) {
        say(`Chưa gán mã ${code} cho món nào`)
        return
      }
      await add(found, null)
      say(`+ ${found.name}`)
    },
  })

  async function bump(item: OrderItem, delta: number) {
    if (!order) return
    await saveItems(order.id, changeQty(items, item.id, delta * QTY_UNIT))
  }

  async function drop(itemId: string) {
    if (!order) return
    await saveItems(
      order.id,
      items.filter((i) => i.id !== itemId),
    )
  }

  async function clearCart() {
    if (!order) return
    await saveItems(order.id, [])
    say('Đã xoá giỏ hàng')
  }

  if (!order) return <div className="screen" />

  return (
    <div className="screen">
      <div className="board quick-board">
        <PageHead
          to="/"
          icon="⚡"
          tone="is-amber"
          title="Bán nhanh"
          sub="Tạo đơn và thu tiền ngay tại quầy"
        />

        <div className="quick-split">
          <div className="quick-main">
            <div className="zone-row">
              <div className="chip-row">
              <button
                type="button"
                className="chip"
                aria-pressed={catId === null && !query}
                onClick={() => {
                  setCatId(null)
                  setQuery('')
                  setSearching(false)
                }}
              >
                Tất cả
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="chip"
                  aria-pressed={catId === c.id && !query}
                  onClick={() => {
                    setCatId(c.id)
                    setQuery('')
                    setSearching(false)
                  }}
                >
                  {c.name}
                </button>
              ))}
              </div>

              {/*
                Tìm món thu về một cái kính lúp.
                Ô gõ chỉ bung ra khi cần, và bung ra ngay tại chỗ chứ không nổi đè lên
                lưới món — gõ ở trên, món hiện ngay bên dưới, không che mất thứ đang tìm.
                Máy quét mã vạch vẫn chạy dù ô này đóng, không cần bấm vào đâu cả.
              */}
              <button
                type="button"
                className="cat-search"
                aria-expanded={searching}
                aria-label="Tìm món"
                title="Tìm món, hoặc quét mã vạch"
                onClick={() => {
                  if (searching) setQuery('')
                  setSearching((v) => !v)
                }}
              >
                <svg viewBox="0 0 24 24" className="glyph" aria-hidden="true" focusable="false">
                  <path
                    d={searching ? 'M6.5 6.5l11 11M17.5 6.5l-11 11' : 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4.2-4.2'}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {searching && (
              <div className="menu-search">
                <span className="search-scan" aria-hidden="true" title="Quét mã vạch được ngay, không cần bấm vào đây">
                  ▮▮▮
                </span>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Tìm món, hoặc quét mã vạch…"
                  inputMode="search"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') return
                    setQuery('')
                    setSearching(false)
                  }}
                />
              </div>
            )}

            {listed.length === 0 ? (
              <div className="empty">
                <div className="empty-icon" aria-hidden="true">
                  🔍
                </div>
                <p>{query ? `Không có món nào khớp "${query}".` : 'Nhóm này chưa có món nào.'}</p>
              </div>
            ) : (
              <>
                <div className="quick-grid">
                  {shown.map((p) => {
                  const inCart = items
                    .filter((i) => i.productId === p.id)
                    .reduce((s, i) => s + i.qtyMilli, 0)

                  return (
                    <div key={p.id} className={`quick-card${inCart > 0 ? ' is-on' : ''}`}>
                      {hotNames.has(p.name) && (
                        <span className="quick-hot" title="Bán chạy hôm nay">
                          <span aria-hidden="true">🔥</span> Bán chạy
                        </span>
                      )}
                      {inCart > 0 && <span className="quick-count num">{qtyToText(inCart)}</span>}

                      <button
                        type="button"
                        className="quick-face"
                        onClick={() => void add(p, null)}
                        aria-label={`Thêm ${p.name}, ${money(p.price)} đồng`}
                      >
                        <Thumb name={p.name} imageKey={p.imageKey} className="quick-thumb" />
                        <b className="quick-name">{p.name}</b>
                        <span className="quick-price num">{money(p.price)}đ</span>
                        <span className="quick-add" aria-hidden="true">
                          +
                        </span>
                      </button>

                      {p.quickOptions.length === 0 ? (
                        <div className="product-opts is-blank" aria-hidden="true" />
                      ) : (
                        <div className="product-opts">
                          {p.quickOptions.map((o) => {
                            const q = qtyOfOption(p.id, o)
                            return (
                              <button
                                key={o}
                                type="button"
                                aria-pressed={q > 0}
                                onClick={() => void add(p, o)}
                              >
                                {o}
                                {q > 0 && <b className="num">{qtyToText(q)}</b>}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                  })}

                  {/* Ô trống cho đủ một trang, để hàng số trang đứng yên một chỗ. */}
                  {pageCount > 1 &&
                    Array.from({ length: PAGE_SIZE - shown.length }, (_, i) => (
                      <div key={`pad-${i}`} className="quick-card is-pad" aria-hidden="true">
                        <span className="quick-face">
                          <span className="quick-thumb" />
                          <b className="quick-name">&nbsp;</b>
                          <span className="quick-price">&nbsp;</span>
                        </span>
                        <div className="product-opts is-blank" />
                      </div>
                    ))}
                </div>

                {pageCount > 1 && (
                  <nav className="menu-pages" aria-label="Trang món">
                    {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                      <button
                        key={n}
                        type="button"
                        className="menu-page num"
                        aria-current={n === page ? 'page' : undefined}
                        onClick={() => setPageAt(n)}
                      >
                        {n}
                      </button>
                    ))}
                  </nav>
                )}
              </>
            )}
          </div>

          <aside className="cart-pane" aria-label="Giỏ hàng">
            <div className="pane-head">
              <span className="pane-name">🧾 Đơn hàng</span>
              {items.length > 0 && <b className="pane-count">{countText}</b>}
              <span className="pane-seats">{order.code}</span>
              {items.length > 0 && (
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => void clearCart()}
                  aria-label="Xoá giỏ hàng"
                  title="Xoá giỏ hàng"
                >
                  🗑
                </button>
              )}
            </div>

            {items.length === 0 ? (
              <div className="pane-empty">
                <div className="empty-icon" aria-hidden="true">
                  🛒
                </div>
                <p>Bấm vào món bên trái, hoặc quét mã vạch để thêm vào đơn.</p>
              </div>
            ) : (
              <>
                <div className="pane-lines">
                  {items.map((i) => (
                    <div key={i.id} className="pane-line">
                      <Thumb name={i.name} imageKey={imageOf(i.productId)} className="pane-thumb" />

                      <div className="pane-line-main">
                        <button type="button" className="line-note" onClick={() => setNoting(i)}>
                          <b>
                            {i.name}
                            {i.optionLabel && <em> · {i.optionLabel}</em>}
                          </b>
                          {i.note ? (
                            <small className="line-note-text">{i.note}</small>
                          ) : (
                            <small className="line-note-add">+ ghi chú</small>
                          )}
                        </button>
                        <div className="stepper">
                          <button type="button" onClick={() => void bump(i, -1)} aria-label={`Bớt ${i.name}`}>
                            −
                          </button>
                          <span className="num">{qtyToText(i.qtyMilli)}</span>
                          <button type="button" onClick={() => void bump(i, 1)} aria-label={`Thêm ${i.name}`}>
                            +
                          </button>
                        </div>
                      </div>

                      <span className="pane-line-total num">{money(i.lineTotal)}</span>

                      <button
                        type="button"
                        className="pane-drop"
                        onClick={() => void drop(i.id)}
                        aria-label={`Bỏ ${i.name} khỏi đơn`}
                        title="Bỏ món"
                      >
                        🗑
                      </button>
                    </div>
                  ))}
                </div>

                <div className="cart-sums">
                  <div className="pay-row">
                    <span>Tạm tính</span>
                    <b className="num">{money(order.subtotal)}</b>
                  </div>
                  <button type="button" className="pay-row is-link" onClick={() => setDiscounting(true)}>
                    <span>{order.discount > 0 ? (order.discountLabel ?? 'Giảm giá') : 'Giảm giá'}</span>
                    {order.discount > 0 ? (
                      <b className="num">−{money(order.discount)}</b>
                    ) : (
                      <b>Bấm để bớt</b>
                    )}
                  </button>
                  {(order.serviceFee ?? 0) > 0 && (
                    <div className="pay-row">
                      <span>Phí phục vụ ({servicePercentText(order.serviceFeePermille)}%)</span>
                      <b className="num">{money(order.serviceFee)}</b>
                    </div>
                  )}
                  <div className="pay-row big divider">
                    <span>Tổng cộng</span>
                    <b className="num">{money(order.total)}đ</b>
                  </div>
                </div>

                <div className="cart-pay">
                  <div className="seg">
                    <button
                      type="button"
                      aria-pressed={method === 'Cash'}
                      onClick={() => setMethod('Cash')}
                    >
                      💵 Tiền mặt
                    </button>
                    <button
                      type="button"
                      aria-pressed={method === 'Transfer'}
                      onClick={() => setMethod('Transfer')}
                    >
                      📱 Chuyển khoản
                    </button>
                  </div>

                  <button
                    type="button"
                    className="btn btn-brand btn-lg wide"
                    onClick={() => nav(`/pay/${order.id}?m=${method}`)}
                  >
                    Thu tiền · {money(order.total)}đ
                  </button>
                </div>
              </>
            )}
          </aside>
        </div>
      </div>

      {discounting && <DiscountSheet order={order} onClose={() => setDiscounting(false)} />}

      {noting && (
        <NoteSheet
          item={noting}
          onClose={() => setNoting(null)}
          onSave={async (note) => {
            await saveItems(
              order.id,
              items.map((x) => (x.id === noting.id ? { ...x, note } : x)),
            )
          }}
        />
      )}

      {weighing && (
        <WeightSheet
          product={weighing}
          onClose={() => setWeighing(null)}
          onConfirm={async (qtyMilli) => {
            const next: OrderItem[] = [
              ...items,
              {
                id: crypto.randomUUID(),
                productId: weighing.id,
                name: weighing.name,
                unitPrice: weighing.price,
                qtyMilli,
                lineTotal: Math.round((weighing.price * qtyMilli) / QTY_UNIT),
                optionLabel: null,
                note: null,
              },
            ]
            await saveItems(order.id, next)
            setWeighing(null)
          }}
        />
      )}
    </div>
  )
}
